import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../lib/supabase';
import { HOTEL_ID } from '../lib/supabase/configClient';
import { stayService } from '../lib/stay/stayService';
import { X, Info } from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { NotificationSender } from './notifications/NotificationSender';

interface TestButtonsPanelProps {
  config?: any;
}

export const TestButtonsPanel: React.FC<TestButtonsPanelProps> = ({ config }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { os } = useDevice();
  const { resetConsent } = useCookieConsent();
  const isMac = os === 'MacOS';
  const shortcutText = isMac ? 'Command+Option+T' : 'Ctrl+Alt+T';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isTyping = ['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName || '');
      if (isTyping) return;

      const key = e.key.toLowerCase();
      const isShortcutPressed = (isMac ? e.metaKey : e.ctrlKey) && e.altKey && key === 't';
      if (isShortcutPressed) {
        setIsVisible(prev => !prev);
      }
    };
    const handleToggle = () => setIsVisible(prev => !prev);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('toggle-test-panel', handleToggle);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('toggle-test-panel', handleToggle);
    };
  }, [isMac]);

  const handleTestAlert = async () => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const currentTime = new Date().toTimeString().split(' ')[0].substring(0, 8);
      const isCheckoutDay = stayService.isCheckoutDay();
      const hasCheckoutAlertBeenShownToday = stayService.hasCheckoutAlertBeenShownToday();

      const response = await supabaseClient.rpc('get_active_alerts', {
        p_hotel_id: HOTEL_ID,
        p_date: currentDate,
        p_time: currentTime,
        p_checkout: isCheckoutDay && !hasCheckoutAlertBeenShownToday
      });

      if (response.data && response.data.length > 0) {
        console.log('First alert data:', JSON.stringify(response.data[0], null, 2));
      }

      alert(`RPC call completed. Check console for details.\nFound ${response.data?.length || 0} alerts.\n${response.error ? `Error: ${response.error.message}` : ''}\nIs checkout day: ${isCheckoutDay}\nCheckout alert already shown today: ${hasCheckoutAlertBeenShownToday}`);

    } catch (error) {
      console.error('Error calling RPC:', error);
      alert(`Error calling RPC: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleSetCheckoutDay = () => {
    try {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      stayService.setStayDates(yesterday, today);
      alert(`Stay dates set to simulate checkout day:\nStart: ${yesterday.toLocaleDateString()}\nEnd (checkout): ${today.toLocaleDateString()}`);
      localStorage.removeItem('checkout_alert_shown_date');
    } catch (error) {
      console.error('Error setting checkout day:', error);
      alert(`Error setting checkout day: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleReset = () => {
    try {
      localStorage.removeItem('has_visited');
      const startDate = new Date('2025-04-10T12:00:00');
      const endDate = new Date('2025-04-13T23:59:59');
      stayService.setStayDates(startDate, endDate);
      alert(`First visit status reset and test dates set:\n- Start: ${startDate.toLocaleDateString()}\n- End: ${endDate.toLocaleDateString()}\n\nReload the page to see the PWA installation banner.`);
    } catch (error) {
      alert('Error resetting first visit status: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleClearStayDates = () => {
    try {
      stayService.clearStayDates();
      alert('Stay dates cleared. Reload the page to see the behavior on opening.');
    } catch (error) {
      alert('Error clearing stay dates: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleResetCookies = () => {
    try {
      resetConsent();
      alert('Cookie consent has been reset. The cookie banner will appear on the next page load.');
    } catch (error) {
      alert('Error resetting cookie consent: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleTestUpdate = async () => {
    const RECORD_ID = 'a8d4ea11-bd95-4883-9147-9e028a752c7c';
    const NEW_VALUE = "j'ai réussi à update la ligne";

    try {
      const { data: records, error: checkError } = await supabaseClient
        .from('translations_section')
        .select('*')
        .eq('id', RECORD_ID);

      if (checkError) throw new Error(`Failed to check record: ${checkError.message}`);
      if (!records || records.length === 0) throw new Error('Record not found');

      const existingRecord = records[0];

      const { data: updateRecords, error: updateError } = await supabaseClient
        .from('translations_section')
        .update({
          en: NEW_VALUE,
          hotel_id: existingRecord.hotel_id,
          section_id: existingRecord.section_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', RECORD_ID)
        .select();

      if (updateError) throw new Error(`Failed to update record: ${updateError.message}`);
      if (!updateRecords || updateRecords.length === 0) throw new Error('Update returned no data');

      const updateResult = updateRecords[0];

      const { data: verifyRecords, error: verifyError } = await supabaseClient
        .from('translations_section')
        .select('en')
        .eq('id', RECORD_ID);

      if (verifyError) throw new Error(`Failed to verify update: ${verifyError.message}`);
      if (!verifyRecords || verifyRecords.length === 0) throw new Error('Verification returned no data');

      const verifyResult = verifyRecords[0];

      if (verifyResult.en !== NEW_VALUE) {
        throw new Error(`Update verification failed: expected "${NEW_VALUE}" but got "${verifyResult.en}"`);
      }

      alert(`Translation updated successfully!\nBefore: ${existingRecord.en || 'null'}\nAfter: ${verifyResult.en}`);

    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <>
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="fixed bottom-2 left-2 z-[998] opacity-0 hover:opacity-30 transition-opacity text-xs px-2 py-1 rounded bg-black text-white select-none"
        >
          test
        </button>
      )}
    {isVisible && (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 z-[999] max-h-[50vh] overflow-auto">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold">Test Panel</h2>
          <div className="bg-gray-700 px-2 py-1 rounded text-xs">{shortcutText}</div>
        </div>
        <button onClick={() => setIsVisible(false)} className="p-2 hover:bg-gray-700 rounded-full">
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <button onClick={handleTestAlert} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded">Test Alert RPC</button>
        <button onClick={handleSetCheckoutDay} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded">Set Checkout Day</button>
        <button onClick={handleReset} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded">Reset First Visit</button>
        <button onClick={handleClearStayDates} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded">Clear Stay Dates</button>
        <button onClick={handleResetCookies} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded">Reset Cookies</button>
        <button onClick={handleTestUpdate} className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded">Test Update</button>
      </div>

      <div className="mt-4 text-sm">
        <div className="flex items-center gap-2 mb-2">
          <Info size={16} />
          <p>Current state:</p>
        </div>
        <ul className="list-disc pl-5 mt-2 grid grid-cols-1 md:grid-cols-2 gap-1">
          <li>Has visited: {localStorage.getItem('has_visited') ? 'Yes' : 'No'}</li>
          <li>Has stay dates: {stayService.hasStayDates() ? 'Yes' : 'No'}</li>
          <li>Is checkout day: {stayService.isCheckoutDay() ? 'Yes' : 'No'}</li>
          <li>Checkout alert shown today: {stayService.hasCheckoutAlertBeenShownToday() ? 'Yes' : 'No'}</li>
          <li>Dates expired: {stayService.areDatesExpired() ? 'Yes' : 'No'}</li>
          <li>Cookie consent: {localStorage.getItem('cookie_consent') ? 'Set' : 'Not set'}</li>
        </ul>
      </div>

      <div className="mt-4">
        <NotificationSender config={config} />
      </div>
    </div>
    )}
    </>
  );
};
